import { isNativeRuntime, shouldTrackInAppAnalytics } from './trackingRuntime';
import { Capacitor } from '@capacitor/core';
import { recordJourneyAction } from './journeyTracking';

export type ClubRootsEventName =
  | 'demo_view'
  | 'demo_cta_clicked'
  | 'sign_up_started'
  | 'account_created'
  | 'create_team_started'
  | 'fixture_created'
  | 'fixture_import_started'
  | 'fixture_import_completed'
  | 'match_report_created'
  | 'player_added'
  | 'team_created'
  | 'blog_post_created'
  | 'ai_analysis_used'
  | 'ai_analysis_viewed'
  | 'ai_question_asked'
  | 'ai_response_received'
  | 'ai_follow_up_asked'
  | 'training_plan_generated'
  | 'web_vitals'
  | 'team_home_loaded';

type AnalyticsParam = string | number | boolean;
type NormalizedAnalyticsParam = string | number;

type AnalyticsParams = Record<string, AnalyticsParam | null | undefined>;
type CampaignParams = Partial<Pick<AnalyticsParams, 'campaign_source' | 'campaign_medium' | 'campaign_name' | 'campaign_content'>>;

const CAMPAIGN_SESSION_KEY = 'clubroots_campaign_context';
const EVENT_SESSION_KEY_PREFIX = 'clubroots_analytics_event_seen';
const UTM_PARAM_MAP = {
  utm_source: 'campaign_source',
  utm_medium: 'campaign_medium',
  utm_campaign: 'campaign_name',
  utm_content: 'campaign_content',
} as const;

function getCurrentUrlSearchParams(): URLSearchParams | null {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);
  const hashQuery = window.location.hash.includes('?') ? window.location.hash.split('?')[1] : '';
  if (hashQuery) {
    new URLSearchParams(hashQuery).forEach((value, key) => {
      if (!params.has(key)) params.set(key, value);
    });
  }
  return params;
}

function readStoredCampaignContext(): CampaignParams {
  if (typeof window === 'undefined') return {};
  try {
    const stored = window.sessionStorage.getItem(CAMPAIGN_SESSION_KEY);
    return stored ? JSON.parse(stored) as CampaignParams : {};
  } catch {
    return {};
  }
}

/**
 * Reads UTM params from the current URL (before or after the hash) and persists them to
 * sessionStorage. Must be called as early as possible on app boot — before any client-side
 * redirect (e.g. /demo -> /team/demo/home) replaces the URL and strips the query string.
 */
export function captureCampaignContext(): void {
  getCampaignContext(getCurrentUrlSearchParams());
}

/**
 * Same as captureCampaignContext, but for native universal links: the incoming URL never
 * touches window.location, so it must be parsed and captured before navigate() drops it.
 */
export function captureCampaignContextFromUrl(rawUrl: string): void {
  try {
    const url = new URL(rawUrl);
    const hashQuery = url.hash.includes('?') ? url.hash.split('?')[1] : '';
    const params = new URLSearchParams(url.search);
    if (hashQuery) {
      new URLSearchParams(hashQuery).forEach((value, key) => {
        if (!params.has(key)) params.set(key, value);
      });
    }
    getCampaignContext(params);
  } catch {
    // Malformed URL; nothing to capture.
  }
}

function getCampaignContext(searchParams: URLSearchParams | null = getCurrentUrlSearchParams()): CampaignParams {
  const nextContext: CampaignParams = {};

  if (searchParams) {
    Object.entries(UTM_PARAM_MAP).forEach(([utmKey, campaignKey]) => {
      const value = searchParams.get(utmKey)?.trim();
      if (value) nextContext[campaignKey] = value.slice(0, 100);
    });
  }

  if (Object.keys(nextContext).length > 0) {
    if (typeof window !== 'undefined') {
      try {
        window.sessionStorage.setItem(CAMPAIGN_SESSION_KEY, JSON.stringify(nextContext));
      } catch {
        // Session storage may be unavailable; analytics still works without campaign context.
      }
    }
    return nextContext;
  }

  return readStoredCampaignContext();
}

function normalizeParamValue(value: AnalyticsParam): NormalizedAnalyticsParam {
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return value;
}

function sanitizeParams(params?: AnalyticsParams): Record<string, NormalizedAnalyticsParam> | undefined {
  if (!params) return undefined;
  const entries = Object.entries(params)
    .filter(([, value]) => value !== null && value !== undefined)
    .map(([key, value]) => [key, normalizeParamValue(value as AnalyticsParam)] as const);
  if (entries.length === 0) return undefined;
  return Object.fromEntries(entries) as Record<string, NormalizedAnalyticsParam>;
}

function getRuntimePlatform(): string {
  if (!isNativeRuntime()) return 'web';
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') return 'native_ios';
  if (platform === 'android') return 'native_android';
  return `native_${platform}`;
}

function writeTrafficLog(eventName: ClubRootsEventName, params?: Record<string, NormalizedAnalyticsParam>): void {
  if (eventName !== 'demo_view' && eventName !== 'demo_cta_clicked') return;

  void import('./Database').then(({ trafficUpdate }) => {
    trafficUpdate({
      action: eventName,
      eventName,
      user: 'anonymous',
      teamName: params?.page === 'demo' || eventName === 'demo_view' ? 'Demo Team' : undefined,
      details: eventName === 'demo_view' ? 'Anonymous visitor viewed demo team' : 'Anonymous visitor clicked demo CTA',
      page: typeof params?.page === 'string' ? params.page : undefined,
      platform: typeof params?.cr_platform === 'string' ? params.cr_platform : undefined,
      campaignSource: typeof params?.campaign_source === 'string' ? params.campaign_source : undefined,
      campaignMedium: typeof params?.campaign_medium === 'string' ? params.campaign_medium : undefined,
      campaignName: typeof params?.campaign_name === 'string' ? params.campaign_name : undefined,
      campaignContent: typeof params?.campaign_content === 'string' ? params.campaign_content : undefined,
    });
  }).catch((error) => {
    if (import.meta.env.DEV) {
      console.warn(`Traffic log failed (${eventName})`, error);
    }
  });
}

async function trackNativeEvent(
  eventName: ClubRootsEventName,
  params?: Record<string, NormalizedAnalyticsParam>,
): Promise<void> {
  const { FirebaseAnalytics } = await import('@capacitor-firebase/analytics');
  await FirebaseAnalytics.logEvent({
    name: eventName,
    params,
  });
}

async function trackWebEvent(
  eventName: ClubRootsEventName,
  params?: Record<string, NormalizedAnalyticsParam>,
): Promise<void> {
  const [{ getApps, getApp }, { getAnalytics, logEvent }] = await Promise.all([
    import('firebase/app'),
    import('firebase/analytics'),
  ]);

  if (getApps().length === 0) return;

  const analytics = getAnalytics(getApp());
  if (params) {
    logEvent(analytics, eventName, params);
  } else {
    logEvent(analytics, eventName);
  }
}

export async function trackEvent(
  eventName: ClubRootsEventName,
  params?: AnalyticsParams,
): Promise<void> {
  if ([
    'account_created', 'fixture_created', 'fixture_import_completed', 'match_report_created',
    'player_added', 'team_created', 'blog_post_created', 'ai_question_asked',
    'ai_response_received', 'ai_analysis_viewed', 'ai_follow_up_asked', 'training_plan_generated',
  ].includes(eventName)) recordJourneyAction(eventName, 'success');
  if (!shouldTrackInAppAnalytics()) return;

  try {
    const eventParams = sanitizeParams({
      cr_platform: getRuntimePlatform(),
      cr_version: import.meta.env.VITE_VERSION ?? 'dev',
      ...getCampaignContext(),
      ...(params ?? {}),
    });

    if (import.meta.env.DEV) {
      console.info('[analytics]', eventName, eventParams ?? {});
    }

    writeTrafficLog(eventName, eventParams);

    if (isNativeRuntime()) {
      await trackNativeEvent(eventName, eventParams);
    } else {
      await trackWebEvent(eventName, eventParams);
    }
  } catch (error) {
    // Analytics failures should never affect app flows.
    console.warn(`Analytics event failed (${eventName})`, error);
  }
}

export async function trackEventOncePerSession(
  eventName: ClubRootsEventName,
  dedupeKey: string,
  params?: AnalyticsParams,
): Promise<void> {
  if (typeof window !== 'undefined') {
    const storageKey = `${EVENT_SESSION_KEY_PREFIX}_${eventName}_${dedupeKey}`;
    try {
      if (window.sessionStorage.getItem(storageKey)) return;
      window.sessionStorage.setItem(storageKey, '1');
    } catch {
      // If session storage is unavailable, fall back to normal safe analytics logging.
    }
  }

  await trackEvent(eventName, params);
}
